import { Injectable } from '@angular/core';
import { dbConst } from './databaseConstants';
import { FirestoreCrudService } from './crudService';
import { AuthService } from '../auth.service';
import { DocumentData, Firestore, Query, QueryConstraint, QueryFieldFilterConstraint, QueryOrderByConstraint, Timestamp, collection, collectionData, doc, getCountFromServer, getDoc, getDocs, limit, orderBy, query, startAfter, updateDoc, where, writeBatch } from '@angular/fire/firestore';
import { INotification } from 'app/models/notification';
import { INotificationVM } from '../../models/notification';
import { Data } from '@angular/router';
import { Observable, firstValueFrom, lastValueFrom, map, pipe, take, tap } from 'rxjs';
import { notificationConsts } from '../constants';
import { ToastService } from '../toast.service';
import { OrderByDirection } from 'firebase/firestore';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private crudService: FirestoreCrudService<INotification>;

  constructor(
    private firestore: Firestore,
    private toastService: ToastService,
    authService: AuthService) {
    this.crudService = new FirestoreCrudService<INotification>(firestore, authService, dbConst.notifications);
  }

  getNewId(): string {
    return this.crudService.getNewId();
  }

  async add(item: INotification, id: string | undefined = undefined) {
    return await this.crudService.add(item, id);
  }

  async update(item: INotification) {
    return await this.crudService.update(item);
  }

  // async getUnreadCount(barId: string): Promise<number> {
  //   const listRef = collection(this.firestore, dbConst.notifications);
  //   const q = query(listRef,
  //     where('barId', '==', barId),
  //     where('read', '==', false));
  //   const snapshot = await getCountFromServer(q);
  //   return snapshot.data().count;
  // }

  async markAsRead(id: string): Promise<void> {
    const docRef = doc(this.firestore, `${dbConst.notifications}/${id}`);
    return updateDoc(docRef, { read: true });
  }

  async countAllNotifications(barId: string): Promise<number> {
    const listRef = collection(this.firestore, dbConst.notifications);
    const q = query(listRef,
      where('barId', '==', barId));

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }

  async countReadNotifications(barId: string): Promise<number> {
    const listRef = collection(this.firestore, dbConst.notifications);
    const q = query(listRef,
      where('barId', '==', barId),
      where('read', '==', true));

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }

  async countUnreadNotifications(barId: string): Promise<number> {

    const listRef = collection(this.firestore, dbConst.notifications);
    const q = query(listRef,
      where('barId', '==', barId),
      where('read', '==', false));
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }

  async countUnreadBeverageNotifications(barId: string): Promise<number> {

    const listRef = collection(this.firestore, dbConst.notifications);
    const q = query(listRef,
      where('barId', '==', barId),
      where('type', '==', notificationConsts.type_beverage),
      where('read', '==', false));
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }



  async countAllTapNotifications(barId: string): Promise<number> {
    const listRef = collection(this.firestore, dbConst.notifications);
    const q = query(listRef,
      where('barId', '==', barId),
      where('type', '==', notificationConsts.type_tap));

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }

  async countReadTapNotifications(barId: string): Promise<number> {
    const listRef = collection(this.firestore, dbConst.notifications);
    const q = query(listRef,
      where('barId', '==', barId),
      where('read', '==', true),
      where('type', '==', notificationConsts.type_tap));

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }

  async countUnreadTapNotifications(barId: string): Promise<number> {

    const listRef = collection(this.firestore, dbConst.notifications);
    const q = query(listRef,
      where('barId', '==', barId),
      where('read', '==', false),
      where('type', '==', notificationConsts.type_tap));
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }





  async markAllTapAsRead(barId: string) {

    if (!barId || barId.length < 3) {
      this.toastService.warning('No barId provided for marking tap notifications as read', 3000);
      return;
    }

    const listRef = collection(this.firestore, dbConst.notifications);
    const PAGE_SIZE = 500;
    let totalMarked = 0;

    while (true) {
      // No `orderBy` here — equality-only filters avoid the composite-index
      // requirement, and order doesn't matter for marking as read. The loop
      // exits naturally when no unread tap items remain.
      const q = query(listRef,
        where('barId', '==', barId),
        where('read', '==', false),
        where('type', '==', notificationConsts.type_tap),
        limit(PAGE_SIZE));
      const snapshot = await getDocs(q);

      if (snapshot.empty) break;

      const batch = writeBatch(this.firestore);
      snapshot.docs.forEach((docSnap) => {
        batch.update(docSnap.ref, { read: true });
      });
      await batch.commit();
      totalMarked += snapshot.docs.length;

      if (snapshot.docs.length < PAGE_SIZE) break;
    }

    if (totalMarked === 0) {
      this.toastService.warning('No unread tap notifications to mark as read', 3000);
      return;
    }

    this.toastService.toastSuccess(`Marked ${totalMarked} tap notifications as read`);

  }


  async markAllAsRead(barId: string) {

    if (!barId || barId.length < 3) {
      this.toastService.warning('No barId provided for marking tap notifications as read', 3000);
      return;
    }

    const listRef = collection(this.firestore, dbConst.notifications);
    const PAGE_SIZE = 500;
    let totalMarked = 0;

    while (true) {
      // No `orderBy` here — equality-only filters avoid the composite-index
      // requirement (the previous "missing index" comment that lived here).
      // Order doesn't matter for marking as read; the loop exits when no
      // unread items remain.
      const q = query(listRef,
        where('barId', '==', barId),
        where('read', '==', false),
        limit(PAGE_SIZE));
      const snapshot = await getDocs(q);

      if (snapshot.empty) break;

      const batch = writeBatch(this.firestore);
      snapshot.docs.forEach((docSnap) => {
        batch.update(docSnap.ref, { read: true });
      });
      await batch.commit();
      totalMarked += snapshot.docs.length;

      if (snapshot.docs.length < PAGE_SIZE) break;
    }

    if (totalMarked === 0) {
      this.toastService.warning('No unread notifications to mark as read', 3000);
      return;
    }

    this.toastService.toastSuccess(`Marked ${totalMarked} notifications as read`);

  }

  async deleteAllReadTapNotifications(barId: string) {

    if (!barId || barId.length < 3) {
      this.toastService.warning('No barId provided for deleting read tap notifications', 3000);
      return;
    }

    const listRef = collection(this.firestore, dbConst.notifications);
    const PAGE_SIZE = 500;
    let totalDeleted = 0;

    while (true) {
      // Equality-only filters; no orderBy → no composite-index required.
      const q = query(listRef,
        where('barId', '==', barId),
        where('read', '==', true),
        where('type', '==', notificationConsts.type_tap),
        limit(PAGE_SIZE));
      const snapshot = await getDocs(q);

      if (snapshot.empty) break;

      const batch = writeBatch(this.firestore);
      snapshot.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      totalDeleted += snapshot.docs.length;

      if (snapshot.docs.length < PAGE_SIZE) break;
    }

    if (totalDeleted === 0) {
      this.toastService.warning('No documents to delete', 3000);
      return;
    }

    this.toastService.toastSuccess(`Deleted ${totalDeleted} read tap notifications`);
  }

  async deleteAllReadNotifications(barId: string) {

    if (!barId || barId.length < 3) {
      this.toastService.warning('No barId provided for deleting read notifications', 3000);
      return;
    }

    const listRef = collection(this.firestore, dbConst.notifications);
    const PAGE_SIZE = 500;
    let totalDeleted = 0;

    while (true) {
      const q = query(listRef,
        where('barId', '==', barId),
        where('read', '==', true),
        limit(PAGE_SIZE));
      const snapshot = await getDocs(q);

      if (snapshot.empty) break;

      const batch = writeBatch(this.firestore);
      snapshot.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      totalDeleted += snapshot.docs.length;

      if (snapshot.docs.length < PAGE_SIZE) break;
    }

    if (totalDeleted === 0) {
      this.toastService.warning('No documents to delete', 3000);
      return;
    }

    this.toastService.toastSuccess(`Deleted ${totalDeleted} read notifications`);
  }

  async deleteAllTapNotifications(barId: string) {

    if (!barId || barId.length < 3) {
      this.toastService.warning('No barId provided for deleting tap notifications', 3000);
      return;
    }

    const listRef = collection(this.firestore, dbConst.notifications);
    const PAGE_SIZE = 500;
    let totalDeleted = 0;

    while (true) {
      const q = query(listRef,
        where('barId', '==', barId),
        where('type', '==', notificationConsts.type_tap),
        limit(PAGE_SIZE));
      const snapshot = await getDocs(q);

      if (snapshot.empty) break;

      const batch = writeBatch(this.firestore);
      snapshot.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      totalDeleted += snapshot.docs.length;

      if (snapshot.docs.length < PAGE_SIZE) break;
    }

    if (totalDeleted === 0) {
      this.toastService.warning('No documents to delete', 3000);
      return;
    }

    this.toastService.toastSuccess(`Deleted ${totalDeleted} tap notifications`);
  }

  async deleteAllNotifications(barId: string) {

    if (!barId || barId.length < 3) {
      this.toastService.warning('No barId provided for deleting notifications', 3000);
      return;
    }

    let totalDeleted = 0;

    const listRef = collection(this.firestore, dbConst.notifications);

    // First batch: Delete READ notifications (matches getReadItemsWithTake pattern)
    const readQuery = query(listRef,
      where('barId', '==', barId),
      where('read', '==', true),
      orderBy('uDate', 'desc'),
      limit(250)); // Split limit between two queries

    const readSnapshot = await getDocs(readQuery);

    if (!readSnapshot.empty) {
      const readBatch = writeBatch(this.firestore);
      readSnapshot.docs.forEach((doc) => {
        readBatch.delete(doc.ref);
      });
      await readBatch.commit();
      totalDeleted += readSnapshot.docs.length;
    }

    // Second batch: Delete UNREAD notifications (matches getUnreadItemsWithTake pattern)
    const unreadQuery = query(listRef,
      where('barId', '==', barId),
      where('read', '==', false),
      orderBy('uDate', 'desc'),
      limit(250)); // Split limit between two queries

    const unreadSnapshot = await getDocs(unreadQuery);

    if (!unreadSnapshot.empty) {
      const unreadBatch = writeBatch(this.firestore);
      unreadSnapshot.docs.forEach((doc) => {
        unreadBatch.delete(doc.ref);
      });
      await unreadBatch.commit();
      totalDeleted += unreadSnapshot.docs.length;
    }

    if (totalDeleted === 0) {
      this.toastService.warning('No documents to delete', 3000);
      return;
    }

    this.toastService.toastSuccess(`Deleted ${totalDeleted} notifications`);
  }

  async softUpdate(id: string, item: any, uid: string) {
    return await this.crudService.softUpdate(id, item, uid);
  }

  async delete(id: string): Promise<void> {
    return await this.crudService.delete(id);
  }


  getItem(id: string) {
    return this.crudService.get(id);
  }

  getItemOnce(id: string) {
    return this.crudService.getOnce(id);
  }

  getItems(barId: string) {
    return this.crudService.getItemsOrderBy(barId, 'uDate', 'desc');
  }

  getItemsWithTake(barId: string, take: number) {
    return this.crudService.getItemsOrderBy(barId, 'uDate', 'desc', take);
  }

  async getUnreadItemsWithTakeFrom(barId: string, take: number, startAfterId: string) {

    const listRef = collection(this.firestore, dbConst.notifications);
    const collectionId = `${dbConst.notifications}/${startAfterId}`;
    const startAfterRef = doc(this.firestore, collectionId);
    const startAfterSnap = await getDoc(startAfterRef);

    const q = query(listRef,
      where('barId', '==', barId),
      where('read', '==', false),
      orderBy('uDate', 'desc'),
      limit(take),
      startAfter(startAfterSnap));
    const sub = collectionData(q, { idField: 'id' })
      .pipe(
        map(changes => {
          return changes.map(a => {
            const data = a as any;
            Object.keys(data).filter(key => Boolean(data[key]?.seconds))
              .forEach(key => {
                if (data[key].seconds) {
                  const date = new Timestamp(data[key].seconds, data[key].nanoseconds);
                  data[key] = date.toDate();
                }
              });
            Object.keys(data).filter(key => data[key] instanceof Timestamp)
              .forEach(key => data[key] = data[key].toDate())
            return data;
          });
        })
      ) as Observable<INotification[]>;

    return await firstValueFrom(sub);
  }

  getUnreadItemsWithTake(barId: string, take: number): Observable<INotification[]> {

    const wheres: QueryFieldFilterConstraint[] = [
      where('barId', '==', barId),
      where('read', '==', false)
    ];

    const orderBys: QueryOrderByConstraint[] = [
      orderBy('uDate', 'desc'),
    ];

    const limits: QueryConstraint[] = [
      limit(take)
    ]

    return this.crudService.getItemsDynamic(wheres, orderBys, limits);
  }

  async getReadItemsWithTakeFrom(barId: string, take: number, startAfterId: string) {

    const listRef = collection(this.firestore, dbConst.notifications);
    const collectionId = `${dbConst.notifications}/${startAfterId}`;
    const startAfterRef = doc(this.firestore, collectionId);
    const startAfterSnap = await getDoc(startAfterRef);

    const q = query(listRef,
      where('barId', '==', barId),
      where('read', '==', true),
      orderBy('uDate', 'desc'),
      limit(take),
      startAfter(startAfterSnap));
    const sub = collectionData(q, { idField: 'id' })
      .pipe(
        map(changes => {
          return changes.map(a => {
            const data = a as any;
            Object.keys(data).filter(key => Boolean(data[key]?.seconds))
              .forEach(key => {
                if (data[key].seconds) {
                  const date = new Timestamp(data[key].seconds, data[key].nanoseconds);
                  data[key] = date.toDate();
                }
              });
            Object.keys(data).filter(key => data[key] instanceof Timestamp)
              .forEach(key => data[key] = data[key].toDate())
            return data;
          });
        })
      ) as Observable<INotification[]>;

    return await firstValueFrom(sub);
  }

  getReadItemsWithTake(barId: string, take: number): Observable<INotification[]> {

    const wheres: QueryFieldFilterConstraint[] = [
      where('barId', '==', barId),
      where('read', '==', true)
    ];

    const orderBys: QueryOrderByConstraint[] = [
      orderBy('uDate', 'desc'),
    ];

    const limits: QueryConstraint[] = [
      limit(take)
    ]

    return this.crudService.getItemsDynamic(wheres, orderBys, limits);

  }


  async getAllItemsWithTakeFrom(barId: string, take: number, startAfterId: string) {

    const listRef = collection(this.firestore, dbConst.notifications);
    const collectionId = `${dbConst.notifications}/${startAfterId}`;
    const startAfterRef = doc(this.firestore, collectionId);
    const startAfterSnap = await getDoc(startAfterRef);

    const q = query(listRef,
      where('barId', '==', barId),
      orderBy('uDate', 'desc'),
      limit(take),
      startAfter(startAfterSnap));
    const sub = collectionData(q, { idField: 'id' })
      .pipe(
        map(changes => {
          return changes.map(a => {
            const data = a as any;
            Object.keys(data).filter(key => Boolean(data[key]?.seconds))
              .forEach(key => {
                if (data[key].seconds) {
                  const date = new Timestamp(data[key].seconds, data[key].nanoseconds);
                  data[key] = date.toDate();
                }
              });
            Object.keys(data).filter(key => data[key] instanceof Timestamp)
              .forEach(key => data[key] = data[key].toDate())
            return data;
          });
        })
      ) as Observable<INotification[]>;

    return await firstValueFrom(sub);
  }

  getItemsOnce(barId: string) {
    return this.crudService.getItemsOnce(barId, 'uDate', 20);
  }

  getItemsPromise(barId: string) {
    return this.crudService.getItemsOnce(barId, 'uDate', 20);
  }


}

